/**
 * tpWorkbookParser.ts
 *
 * Client-side parser for the vendor-supplied TP workbook (.xlsx).
 *
 * Expected sheets:
 *   "1. Test Procedure"       — one row per DNV clause (5.4.2 – 5.4.18)
 *   "2. Comm. with Untrust TP" — one row per SR for section 4.11
 *   "Column B"                 — column legend / reference (not parsed)
 *
 * Section-key mapping (Excel clause → Word section → internal key):
 *   5.4.2  → 4.1  → system_security_functions
 *   5.4.3  → 4.2  → vessel_asset_inventory          (parsed but not template-driven)
 *   5.4.4  → 4.3  → malicious_code_protection
 *   5.4.5  → 4.4  → hardening_and_configuration
 *   5.4.6  → 4.5  → physical_access_control
 *   5.4.7  → 4.6  → logical_access_control
 *   5.4.8  → 4.7  → network_segmentation
 *   5.4.9  → 4.8  → zone_boundary_control
 *   5.4.10 → 4.9  → application_partitioning
 *   5.4.11 → 4.10 → denial_of_service
 *   5.4.12 → 4.11 → communication_with_untrusted_networks  (sheet 2 aggregated)
 *   5.4.13 → 4.12 → wireless_networks
 *   5.4.14 → 4.13 → mobile_and_portable_devices
 *   5.4.15 → 4.14 → monitoring_and_diagnostics_functions
 *   5.4.16 → 4.15 → verification_of_security_functions
 *   5.4.17 → 4.16 → incident_response_and_recovery
 *   5.4.18 → UNSUPPORTED (Systems excluded based on negligible risk)
 *
 * 4.11 mismatch note:
 *   Word TP section 4.11 lists SR 3.8 RE1 in its requirements, but the
 *   Excel sheet "2. Comm. with Untrust TP" may not have a separate row for
 *   SR 3.8 RE1. parser aggregates whatever rows ARE present in the sheet.
 *   missing SR rows aren't synthesized. check tpWorkbookResult.warnings for
 *   reported mismatches.
 */

import * as XLSX from 'xlsx'

// ── Types ──────────────────────────────────────────────────────────────────

export interface TPSectionContent {
  test_setup: string
  initial_condition: string
  test_equipment: string
  detailed_steps: string
  expected_result: string
}

export type TPSectionKey =
  | 'system_security_functions'
  | 'vessel_asset_inventory'
  | 'malicious_code_protection'
  | 'hardening_and_configuration'
  | 'physical_access_control'
  | 'logical_access_control'
  | 'network_segmentation'
  | 'zone_boundary_control'
  | 'application_partitioning'
  | 'denial_of_service'
  | 'communication_with_untrusted_networks'
  | 'wireless_networks'
  | 'mobile_and_portable_devices'
  | 'monitoring_and_diagnostics_functions'
  | 'verification_of_security_functions'
  | 'incident_response_and_recovery'

export interface TPWorkbookResult {
  sections: Partial<Record<TPSectionKey, TPSectionContent>>
  warnings: string[]
  unsupported: string[]    // Excel rows with no matching Word section
  parsedSheets: string[]   // names of sheets that were successfully read
  comm411RowCount: number  // number of SR rows aggregated for section 4.11
}

// ── Section-key map: clause number/title fragment → section key ────────────

const CLAUSE_TO_SECTION_KEY: Record<string, TPSectionKey> = {
  '5.4.2': 'system_security_functions',
  '5.4.3': 'vessel_asset_inventory',
  '5.4.4': 'malicious_code_protection',
  '5.4.5': 'hardening_and_configuration',
  '5.4.6': 'physical_access_control',
  '5.4.7': 'logical_access_control',
  '5.4.8': 'network_segmentation',
  '5.4.9': 'zone_boundary_control',
  '5.4.10': 'application_partitioning',
  '5.4.11': 'denial_of_service',
  '5.4.12': 'communication_with_untrusted_networks',
  '5.4.13': 'wireless_networks',
  '5.4.14': 'mobile_and_portable_devices',
  '5.4.15': 'monitoring_and_diagnostics_functions',
  '5.4.16': 'verification_of_security_functions',
  '5.4.17': 'incident_response_and_recovery',
  '5.4.18': '__UNSUPPORTED__' as any,
}

// also match by title keywords as fallback
const TITLE_KEYWORDS_TO_KEY: Array<[string, TPSectionKey]> = [
  ['system security function',      'system_security_functions'],
  ['vessel asset inventory',        'vessel_asset_inventory'],
  ['malicious code',                'malicious_code_protection'],
  ['hardening',                     'hardening_and_configuration'],
  ['physical access',               'physical_access_control'],
  ['logical access',                'logical_access_control'],
  ['network segmentation',          'network_segmentation'],
  ['zone boundary',                 'zone_boundary_control'],
  ['application partition',         'application_partitioning'],
  ['denial of service',             'denial_of_service'],
  ['communication with untrusted',  'communication_with_untrusted_networks'],
  ['wireless network',              'wireless_networks'],
  ['mobile and portable',           'mobile_and_portable_devices'],
  ['monitoring and diagnostic',     'monitoring_and_diagnostics_functions'],
  ['verification of security',      'verification_of_security_functions'],
  ['incident response',             'incident_response_and_recovery'],
]

// ── Word's 4.11 expected SR rows for mismatch detection ───────────────────
const WORD_411_EXPECTED_SRS = [
  'SR 1.1 RE2', 'SR 1.2', 'SR 1.11', 'SR 1.12',
  'SR 1.13', 'SR 1.13 RE1', 'SR 2.6', 'SR 2.8',
  'SR 3.1 RE1', 'SR 3.5', 'SR 3.8', 'SR 3.8 RE1',
]

// ── Helpers ────────────────────────────────────────────────────────────────

function str(val: unknown): string {
  if (val === null || val === undefined) return ''
  return String(val).trim()
}

/** find a column index in a header row by matching keyword(s) */
function findCol(headers: string[], ...keywords: string[]): number {
  const lc = headers.map(h => h.toLowerCase())
  for (const kw of keywords) {
    const idx = lc.findIndex(h => h.includes(kw.toLowerCase()))
    if (idx !== -1) return idx
  }
  return -1
}

/** resolve a row's identifier (clause number or section title) to a section key */
function resolveKey(identifier: string): TPSectionKey | '__UNSUPPORTED__' | null {
  const id = identifier.trim()

  // try clause number match (e.g. "5.4.2", "5.4.2 System security functions")
  for (const [clause, key] of Object.entries(CLAUSE_TO_SECTION_KEY)) {
    if (id.startsWith(clause)) return key as any
  }

  // try title keyword match (case-insensitive)
  const lower = id.toLowerCase()
  for (const [kw, key] of TITLE_KEYWORDS_TO_KEY) {
    if (lower.includes(kw)) return key
  }

  return null
}

// ── Sheet 1 parser ─────────────────────────────────────────────────────────

function parseSheet1(
  wb: XLSX.WorkBook,
  warnings: string[],
  unsupported: string[],
): Partial<Record<TPSectionKey, TPSectionContent>> {
  const sheetName = '1. Test Procedure'
  const sheet = wb.Sheets[sheetName]
  if (!sheet) {
    warnings.push(`Sheet "${sheetName}" not found in workbook`)
    return {}
  }

  const rows: unknown[][] = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' })
  if (rows.length < 2) {
    warnings.push(`Sheet "${sheetName}" has fewer than 2 rows — no data`)
    return {}
  }

  // Row 0 = headers
  const headers = (rows[0] as string[]).map(str)

  // detect columns by header keywords.
  // the workbook may call the identifier column "No.", "Clause", "Section", etc.
  let idCol   = findCol(headers, 'no.', 'clause', 'section', 'item', '5.4')
  if (idCol === -1) idCol = 0  // fall back to first column

  const setupCol     = findCol(headers, 'necessary test setup', 'test setup', 'setup')
  const conditionCol = findCol(headers, 'initial condition', 'condition')
  const equipCol     = findCol(headers, 'test equipment', 'equipment')
  const stepsCol     = findCol(headers, 'detailed step', 'step')
  const resultCol    = findCol(headers, 'expected result', 'result')

  const sections: Partial<Record<TPSectionKey, TPSectionContent>> = {}

  for (let r = 1; r < rows.length; r++) {
    const row = rows[r] as unknown[]
    const identifier = str(row[idCol])
    if (!identifier) continue

    const key = resolveKey(identifier)
    if (!key) {
      warnings.push(`Sheet 1 row ${r + 1}: unrecognised identifier "${identifier}" — skipped`)
      continue
    }
    if (key === '__UNSUPPORTED__') {
      unsupported.push(`5.4.18 (Systems excluded based on negligible risk) — no matching Word TP section; row ${r + 1} skipped`)
      continue
    }

    // extract field values using detected column positions, or positional fallback
    const getCell = (col: number, fallbackOffset: number): string =>
      col !== -1 ? str(row[col]) : str(row[idCol + fallbackOffset] ?? '')

    sections[key as TPSectionKey] = {
      test_setup:        getCell(setupCol,     1),
      initial_condition: getCell(conditionCol, 2),
      test_equipment:    getCell(equipCol,     3),
      detailed_steps:    getCell(stepsCol,     4),
      expected_result:   getCell(resultCol,    5),
    }
  }

  return sections
}

// ── Sheet 2 parser (4.11 aggregation) ─────────────────────────────────────

function parseSheet2(
  wb: XLSX.WorkBook,
  warnings: string[],
): TPSectionContent | null {
  const sheetName = '2. Comm. with Untrust TP'
  const sheet = wb.Sheets[sheetName]
  if (!sheet) {
    warnings.push(`Sheet "${sheetName}" not found — 4.11 content will be empty`)
    return null
  }

  const rows: unknown[][] = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' })
  if (rows.length < 2) {
    warnings.push(`Sheet "${sheetName}" has fewer than 2 rows`)
    return null
  }

  const headers = (rows[0] as string[]).map(str)

  // detect SR identifier column and field columns
  let srCol       = findCol(headers, 'sr', 'req', 'clause', 'no.')
  if (srCol === -1) srCol = 0

  const setupCol     = findCol(headers, 'necessary test setup', 'test setup', 'setup')
  const conditionCol = findCol(headers, 'initial condition', 'condition')
  const equipCol     = findCol(headers, 'test equipment', 'equipment')
  const stepsCol     = findCol(headers, 'detailed step', 'step')
  const resultCol    = findCol(headers, 'expected result', 'result')

  const aggregated: Record<keyof TPSectionContent, string[]> = {
    test_setup: [], initial_condition: [], test_equipment: [],
    detailed_steps: [], expected_result: [],
  }

  const foundSRs: string[] = []
  let parsedRowCount = 0

  for (let r = 1; r < rows.length; r++) {
    const row = rows[r] as unknown[]
    const srLabel = str(row[srCol])
    if (!srLabel) continue

    foundSRs.push(srLabel)
    parsedRowCount++
    const prefix = `${r}. [${srLabel}]`

    const getCell = (col: number, fallbackOffset: number): string =>
      col !== -1 ? str(row[col]) : str(row[srCol + fallbackOffset] ?? '')

    const setup    = getCell(setupCol,     1)
    const cond     = getCell(conditionCol, 2)
    const equip    = getCell(equipCol,     3)
    const steps    = getCell(stepsCol,     4)
    const result   = getCell(resultCol,    5)

    if (setup)  aggregated.test_setup.push(`${prefix} ${setup}`)
    if (cond)   aggregated.initial_condition.push(`${prefix} ${cond}`)
    if (equip)  aggregated.test_equipment.push(`${prefix} ${equip}`)
    if (steps)  aggregated.detailed_steps.push(`${prefix} ${steps}`)
    if (result) aggregated.expected_result.push(`${prefix} ${result}`)
  }

  // report 4.11 mismatch: SRs in Word but not in Excel
  const missingSRs = WORD_411_EXPECTED_SRS.filter(
    sr => !foundSRs.some(found => found.includes(sr.replace('SR ', '').trim()))
  )
  if (missingSRs.length > 0) {
    warnings.push(
      `4.11 mismatch: Word TP lists these SRs not found in "${sheetName}": ${missingSRs.join(', ')}. ` +
      `Most notably SR 3.8 RE1 may be missing from the Excel sheet.`
    )
  }

  return {
    _rowCount: parsedRowCount, // internal, read by parseTPWorkbook for comm411RowCount
    test_setup:        aggregated.test_setup.join('\n'),
    initial_condition: aggregated.initial_condition.join('\n'),
    test_equipment:    aggregated.test_equipment.join('\n'),
    detailed_steps:    aggregated.detailed_steps.join('\n'),
    expected_result:   aggregated.expected_result.join('\n'),
  } as any
}

// ── Main export ────────────────────────────────────────────────────────────

/**
 * parse a vendor TP workbook file, extract structured section content.
 *
 * @param file  .xlsx File object from a browser <input type="file">
 * @returns     structured TPWorkbookResult with sections, warnings, unsupported items
 */
export async function parseTPWorkbook(file: File): Promise<TPWorkbookResult> {
  const buffer = await file.arrayBuffer()
  const wb = XLSX.read(buffer, { type: 'array' })

  const warnings: string[]   = []
  const unsupported: string[] = []
  const parsedSheets: string[] = []

  // parse sheet 1
  const sections = parseSheet1(wb, warnings, unsupported)
  if (wb.Sheets['1. Test Procedure']) parsedSheets.push('1. Test Procedure')

  // parse sheet 2, merge into 4.11
  let comm411RowCount = 0
  const comm411Raw = parseSheet2(wb, warnings)
  if (comm411Raw) {
    comm411RowCount = (comm411Raw as any)._rowCount ?? 0
    const { _rowCount: _, ...comm411Content } = comm411Raw as any
    sections['communication_with_untrusted_networks'] = comm411Content
    parsedSheets.push('2. Comm. with Untrust TP')
  }

  // "Column B" sheet is informational only
  if (wb.Sheets['Column B']) {
    parsedSheets.push('Column B (informational, not parsed)')
  }

  // warn about vessel_asset_inventory (4.2), parsed but template-driven, not placeholder-driven
  if (sections['vessel_asset_inventory']) {
    warnings.push(
      'Section 4.2 (Vessel Asset Inventory) was found in the workbook but is not ' +
      'placeholder-driven in the template — it uses an appendix table. Content ignored.'
    )
    delete sections['vessel_asset_inventory']
  }

  return { sections, warnings, unsupported, parsedSheets, comm411RowCount }
}

/**
 * flatten a TPWorkbookResult.sections into a plain object for the API request payload.
 * keys follow the pattern: <section_key>_<field_name>
 */
export function flattenTPSections(
  sections: Partial<Record<TPSectionKey, TPSectionContent>>
): Record<string, string> {
  const flat: Record<string, string> = {}
  for (const [sectionKey, content] of Object.entries(sections)) {
    if (!content) continue
    for (const [field, value] of Object.entries(content)) {
      flat[`${sectionKey}_${field}`] = value ?? ''
    }
  }
  return flat
}
